type DbConfig = {
  dbName: string;
  storeName: string;
  version: number;
};

const DB: DbConfig = {
  dbName: 'neptune_db',
  storeName: 'neptune_attribute_search_space_trace',
  version: 1,
};

type StoredValue<T> = {
  value: T;
};

const IDB_IDLE_CLOSE_MS = 60_000;

let cachedDb: IDBDatabase | null = null;
let openingDb: Promise<IDBDatabase> | null = null;
let idleCloseTimer: number | null = null;

function clearIdleCloseTimer() {
  if (idleCloseTimer != null) {
    window.clearTimeout(idleCloseTimer);
    idleCloseTimer = null;
  }
}

function closeCachedDb() {
  clearIdleCloseTimer();

  if (cachedDb != null) {
    cachedDb.close();
    cachedDb = null;
  }
}

function scheduleIdleClose() {
  clearIdleCloseTimer();

  idleCloseTimer = window.setTimeout(() => {
    idleCloseTimer = null;
    closeCachedDb();
  }, IDB_IDLE_CLOSE_MS);
}

function openDb(): Promise<IDBDatabase> {
  if (typeof window === 'undefined' || window.indexedDB == null) {
    return Promise.reject(new Error('IndexedDB not available'));
  }

  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB.dbName, DB.version);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(DB.storeName)) {
        db.createObjectStore(DB.storeName);
      }
    };

    request.onblocked = () => {
      // Another open connection (e.g. other tab) is preventing open/upgrade.
      // We keep this best-effort; callers already ignore persistence errors.
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Failed to open IndexedDB'));
  });
}

async function getDb(): Promise<IDBDatabase> {
  if (cachedDb != null) {
    scheduleIdleClose();
    return cachedDb;
  }

  if (openingDb != null) {
    return openingDb;
  }

  openingDb = openDb()
    .then((db) => {
      cachedDb = db;

      // Ensure schema upgrades (version bumps) do not get stuck.
      db.onversionchange = () => {
        closeCachedDb();
      };

      scheduleIdleClose();
      return db;
    })
    .finally(() => {
      openingDb = null;
    });

  return openingDb;
}

function isRetryableIndexedDbError(error: unknown): boolean {
  if (error instanceof DOMException) {
    return error.name === 'InvalidStateError' || error.name === 'AbortError';
  }

  return false;
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  async function attemptOnce(): Promise<T> {
    const db = await getDb();

    scheduleIdleClose();

    return await new Promise((resolve, reject) => {
      const tx = db.transaction(DB.storeName, mode);
      const store = tx.objectStore(DB.storeName);
      const request = fn(store);

      let requestResult: T;
      let requestDone = false;

      tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));

      tx.oncomplete = () => {
        if (requestDone) {
          resolve(requestResult);
        }
      };

      request.onsuccess = () => {
        requestResult = request.result;
        requestDone = true;
      };

      request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
    });
  }

  try {
    return await attemptOnce();
  } catch (error) {
    if (isRetryableIndexedDbError(error)) {
      closeCachedDb();
      return await attemptOnce();
    }

    throw error;
  }
}

export async function getAttributeSearchSpaceTraceFromDb<T>(
  username: string,
): Promise<T | undefined> {
  const result = await withStore<StoredValue<T> | undefined>('readonly', (store) =>
    store.get(username),
  );
  return result?.value;
}

export async function setAttributeSearchSpaceTraceInDb<T>(
  username: string,
  value: T,
): Promise<void> {
  await withStore<IDBValidKey>('readwrite', (store) =>
    store.put({ value } satisfies StoredValue<T>, username),
  );
}
